// src/lib/api/ledger.ts

import { getBillPerConsumer } from "@/lib/api/billing"
import { getConsumerPaymentHistoy } from "@/lib/api/payment"
import type { PaginationType, ConsumerBillType } from "@/types"
import type { PaymentHistoryType } from "@/types/payment"

// 1) Unified row type for the account ledger
export interface LedgerEntryType {
  trans_type:   "Billing" | "Payment"
  trans_date:   string        // ISO date string
  reference:    string        // billing_id or payment_id
  reading?:     number        // meter reading, if any
  volume?:      number        // consumption units, if any

  // ← NEW!
  others?:      number        // other charges
  bill_amount?: number        // base bill
  modified_by?: string        // who posted this entry

  debit:        number        // amount billed (bill_amount + others)
  credit:       number        // amount paid
  balance:      number        // running balance
}

// 2) Fetch billing & payment data, merge, sort, filter, compute running balance
export async function getLedgerPerConsumer(
  consumerId: number,
  from:       string,
  to:         string,
  size:       number,
  page:       number,
  search:     string
): Promise<PaginationType<LedgerEntryType>> {
  // A) Fetch billing history
  let billsPage: PaginationType<ConsumerBillType>
  try {
    billsPage = await getBillPerConsumer(
      consumerId,
      from,
      to,
      size,
      page,
      search
    )
  } catch {
    billsPage = { count: 0, next: null, previous: null, results: [] }
  }

  // B) Fetch **only** this consumer’s payments
  let paymentsPage: PaginationType<PaymentHistoryType>
  try {
    paymentsPage = await getConsumerPaymentHistoy(
      consumerId,
      from,
      to,
      size,
      page,
      search
    )
  } catch {
    paymentsPage = { count: 0, next: null, previous: null, results: [] }
  }

  // C) Transform billing records
  const billRows: LedgerEntryType[] = billsPage.results.map((b) => ({
    trans_type:   "Billing",
    trans_date:   b.date,
    reference:    String(b.billing_id),
    reading:      b.units,
    volume:       undefined,

    others:       undefined,
    bill_amount:  b.total_amount,
    modified_by:  undefined,

    debit:        b.total_amount,
    credit:       0,
    balance:      0,
  }))

  // D) Transform payment records
  const payRows: LedgerEntryType[] = paymentsPage.results.map((p) => ({
    trans_type:   "Payment",
    trans_date:   p.payment_date,
    reference:    String(p.payment_id),
    reading:      undefined,
    volume:       undefined,

    others:       undefined,
    bill_amount:  undefined,
    modified_by:  undefined,

    debit:        0,
    credit:       p.amount_paid,
    balance:      0,
  }))

  // E) Merge and sort by date ascending
  const merged = [...billRows, ...payRows].sort(
    (a, b) => new Date(a.trans_date).getTime() - new Date(b.trans_date).getTime()
  )

  // F) Compute running balance
  let running = 0
  const withBalance = merged.map((row) => {
    running += row.debit - row.credit
    return { ...row, balance: running }
  })

  // G) Apply search filter (case‑insensitive)
  const filtered = search
    ? withBalance.filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(search.toLowerCase())
        )
      )
    : withBalance

  // H) Return paginated shape, preserving billing pagination
  return {
    count:    filtered.length,
    next:     billsPage.next,
    previous: billsPage.previous,
    results:  filtered,
  }
}
